import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import PlaceCard from "../components/PlaceCard";
import { Briefcase, Plus, Crown, ShieldCheck } from "lucide-react";
import { CATEGORIES } from "../lib/categories";

const PLAN_BADGES = {
  basic: { label: "Annonce Basique (10 % commission)", color: "bg-brand-sandlight text-brand-ink", icon: null },
  monthly: { label: "Premium Mensuel", color: "bg-brand-terracotta text-white", icon: ShieldCheck },
  yearly: { label: "Premium Annuel", color: "bg-brand-gold text-brand-black", icon: Crown },
};

export default function OwnerDashboardPage() {
  const { user, loading } = useAuth();
  const [places, setPlaces] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (user && (user.role === "owner" || user.role === "admin")) {
      api.get("/owner/my-places")
        .then(({ data }) => setPlaces(data))
        .catch(() => setPlaces([]))
        .finally(() => setFetching(false));
    }
  }, [user]);

  if (loading || user === null) {
    return <div className="p-12 text-center text-brand-inkSoft">Chargement...</div>;
  }
  if (!user || user === false) {
    return <Navigate to="/login" replace />;
  }
  if (user.role !== "owner" && user.role !== "admin") {
    return <Navigate to="/devenir-annonceur" replace />;
  }

  const plan = PLAN_BADGES[user.plan || "basic"] || PLAN_BADGES.basic;
  const PlanIcon = plan.icon;
  const cat = user.business_category ? CATEGORIES[user.business_category] : null;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10" data-testid="owner-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-gold/15 text-brand-goldDark text-xs uppercase tracking-[0.18em] font-bold mb-3">
            <Briefcase className="w-3.5 h-3.5" />
            Espace annonceur
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-ink tracking-tight mb-1">
            Bonjour {user.name} 👋
          </h1>
          {user.business_name && (
            <p className="text-brand-inkSoft">
              {user.business_name}
              {cat && (
                <>
                  {" · "}
                  <span style={{ color: cat.color }} className="font-semibold">{cat.label}</span>
                </>
              )}
              {user.business_city && ` · ${user.business_city}`}
            </p>
          )}
        </div>
        <div
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${plan.color}`}
          data-testid="owner-plan-badge"
        >
          {PlanIcon && <PlanIcon className="w-4 h-4" />}
          {plan.label}
        </div>
      </div>

      {/* Plan call-to-action */}
      {user.plan !== "yearly" && (
        <Card className="rounded-2xl border-brand-line bg-gradient-to-r from-brand-cream to-brand-sandlight p-6 mb-8 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-heading font-semibold text-brand-ink mb-1">
              {user.plan === "monthly"
                ? "Passez à l'annuel et économisez 100 000 FCFA"
                : "Passez en Premium et supprimez la commission de 10 %"}
            </div>
            <p className="text-sm text-brand-inkSoft">
              Annonces illimitées, badge vérifié, 0 % de commission et remontée en tête de liste.
            </p>
          </div>
          <Link to="/tarifs">
            <Button className="rounded-full bg-brand-gold hover:bg-brand-goldDark text-brand-black font-semibold" data-testid="dashboard-see-plans-btn">
              Voir les formules
            </Button>
          </Link>
        </Card>
      )}

      {/* My listings */}
      <section>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl sm:text-2xl font-semibold text-brand-ink tracking-tight">
            Mes annonces
          </h2>
          <Button
            disabled
            className="rounded-full bg-brand-terracotta/60 text-white cursor-not-allowed"
            title="Bientôt disponible"
            data-testid="add-listing-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Nouvelle annonce
          </Button>
        </div>

        {fetching ? (
          <div className="text-center py-12 text-brand-inkMuted">Chargement de vos annonces...</div>
        ) : places.length === 0 ? (
          <Card className="rounded-2xl border-dashed border-2 border-brand-line bg-white p-10 text-center">
            <Briefcase className="w-12 h-12 text-brand-inkMuted mx-auto mb-4" strokeWidth={1.4} />
            <h3 className="font-heading text-lg font-semibold text-brand-ink mb-2">
              Vous n'avez pas encore d'annonce
            </h3>
            <p className="text-sm text-brand-inkSoft max-w-md mx-auto mb-5">
              La création d'annonces par les annonceurs sera disponible très bientôt. En attendant,
              contactez l'équipe Kanama pour ajouter votre commerce manuellement.
            </p>
            <p className="text-xs text-brand-inkMuted">
              📞 Contact équipe : <strong>+235 65 00 00 00</strong> · contact@kanama.td
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {places.map((p) => (
              <PlaceCard key={p.id} place={p} />
            ))}
          </div>
        )}
      </section>

      <p className="text-xs text-brand-inkMuted text-center mt-12">
        ⚠️ Cet espace est en phase pilote. La création/édition d'annonces par les annonceurs et le paiement réel des abonnements arrivent prochainement.
      </p>
    </div>
  );
}
